import React, { useEffect, useRef, useCallback, useState } from 'react';

// ============================================================================
// Types & Interfaces
// ============================================================================

interface Star {
  x: number;
  y: number;
  size: number;
  opacity: number;
  twinkleSpeed: number;
  twinklePhase: number;
  layer: number; // 1-3 for parallax depth
}

interface ShootingStar {
  x: number;
  y: number;
  length: number;
  speed: number;
  angle: number;
  opacity: number;
  active: boolean;
  trail: { x: number; y: number; opacity: number }[];
}

interface Particle {
  x: number;
  y: number;
  size: number;
  opacity: number;
  speedX: number;
  speedY: number;
  color: string;
}

interface CosmicBackgroundProps {
  /** Intensity of animations (0-1), affects star count and animation speeds */
  intensity?: number;
  /** Whether to show the nebula gradient layer */
  showNebula?: boolean;
  /** Whether to enable shooting stars */
  enableShootingStars?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

// Color palette (Catppuccin-inspired cosmic theme)
const COLORS = {
  deepSpace: ['#0d0d1a', '#1a1a2e', '#11111b'],
  nebulaPurple: ['#4c1d95', '#6d28d9', '#7c3aed'],
  nebulaBlue: ['#1e3a8a', '#1d4ed8', '#3b82f6'],
  starWhite: ['#f8fafc', '#e2e8f0', '#cbd5e1'],
  accentGold: '#fbbf24',
};

// Animation settings
const STAR_COUNT_DESKTOP = 150;
const STAR_COUNT_MOBILE = 80;
const PARTICLE_COUNT = 25;
const SHOOTING_STAR_INTERVAL_MIN = 8000; // 8 seconds
const SHOOTING_STAR_INTERVAL_MAX = 12000; // 12 seconds
const PARALLAX_STRENGTH = 0.02;

// ============================================================================
// Utility Functions
// ============================================================================

const random = (min: number, max: number): number => Math.random() * (max - min) + min;

const isMobile = (): boolean => window.innerWidth < 768;

// ============================================================================
// Main Component
// ============================================================================

const CosmicBackground: React.FC<CosmicBackgroundProps> = ({
  intensity = 1,
  showNebula = true,
  enableShootingStars = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>(0);
  const starsRef = useRef<Star[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const shootingStarRef = useRef<ShootingStar | null>(null);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const scrollPositionRef = useRef(0);
  const lastShootingStarTimeRef = useRef(0);
  const nextShootingStarDelayRef = useRef(random(SHOOTING_STAR_INTERVAL_MIN, SHOOTING_STAR_INTERVAL_MAX));

  const [isDarkMode, setIsDarkMode] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // ============================================================================
  // Initialization Functions
  // ============================================================================

  const initializeStars = useCallback((width: number, height: number) => {
    const count = isMobile()
      ? Math.floor(STAR_COUNT_MOBILE * intensity)
      : Math.floor(STAR_COUNT_DESKTOP * intensity);

    const stars: Star[] = [];
    for (let i = 0; i < count; i++) {
      stars.push({
        x: random(0, width),
        y: random(0, height),
        size: random(0.5, 2),
        opacity: random(0.3, 1),
        twinkleSpeed: random(0.5, 2),
        twinklePhase: random(0, Math.PI * 2),
        layer: Math.ceil(random(1, 3)), // 1 = far, 3 = near
      });
    }
    starsRef.current = stars;
  }, [intensity]);

  const initializeParticles = useCallback((width: number, height: number) => {
    const particles: Particle[] = [];
    const count = Math.floor(PARTICLE_COUNT * intensity);

    for (let i = 0; i < count; i++) {
      particles.push({
        x: random(0, width),
        y: random(0, height),
        size: random(1, 3),
        opacity: random(0.1, 0.3),
        speedX: random(-0.2, 0.2),
        speedY: random(-0.1, 0.1),
        color: Math.random() > 0.7
          ? COLORS.nebulaPurple[Math.floor(random(0, 3))]
          : COLORS.nebulaBlue[Math.floor(random(0, 3))],
      });
    }
    particlesRef.current = particles;
  }, [intensity]);

  const createShootingStar = useCallback((width: number, height: number) => {
    // Start from top or right edge
    const startFromTop = Math.random() > 0.5;
    const x = startFromTop ? random(width * 0.1, width * 0.9) : width + 50;
    const y = startFromTop ? -50 : random(0, height * 0.4);

    shootingStarRef.current = {
      x,
      y,
      length: random(80, 150),
      speed: random(15, 25),
      angle: random(Math.PI * 0.6, Math.PI * 0.8), // Diagonal down-left
      opacity: 1,
      active: true,
      trail: [],
    };
  }, []);

  // ============================================================================
  // Drawing Functions
  // ============================================================================

  const drawNebulaGradient = useCallback((
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    time: number
  ) => {
    if (!showNebula) return;

    const opacity = isDarkMode ? 0.15 : 0.05;

    // Slow drift offset
    const driftX = Math.sin(time * 0.0001) * 50;
    const driftY = Math.cos(time * 0.00008) * 30;

    // Purple nebula
    const gradient1 = ctx.createRadialGradient(
      width * 0.3 + driftX, height * 0.4 + driftY, 0,
      width * 0.3 + driftX, height * 0.4 + driftY, width * 0.5
    );
    gradient1.addColorStop(0, `rgba(124, 58, 237, ${opacity})`);
    gradient1.addColorStop(0.5, `rgba(109, 40, 217, ${opacity * 0.5})`);
    gradient1.addColorStop(1, 'rgba(109, 40, 217, 0)');

    ctx.fillStyle = gradient1;
    ctx.fillRect(0, 0, width, height);

    // Blue nebula
    const gradient2 = ctx.createRadialGradient(
      width * 0.7 - driftX, height * 0.6 - driftY, 0,
      width * 0.7 - driftX, height * 0.6 - driftY, width * 0.4
    );
    gradient2.addColorStop(0, `rgba(59, 130, 246, ${opacity})`);
    gradient2.addColorStop(0.5, `rgba(29, 78, 216, ${opacity * 0.5})`);
    gradient2.addColorStop(1, 'rgba(29, 78, 216, 0)');

    ctx.fillStyle = gradient2;
    ctx.fillRect(0, 0, width, height);
  }, [showNebula, isDarkMode]);

  const drawStars = useCallback((
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    time: number
  ) => {
    const mouseX = mousePositionRef.current.x;
    const mouseY = mousePositionRef.current.y;
    const centerX = width / 2;
    const centerY = height / 2;

    starsRef.current.forEach((star) => {
      // Twinkling effect (disabled if reduced motion)
      let twinkle = 1;
      if (!reducedMotion) {
        twinkle = 0.5 + 0.5 * Math.sin(time * 0.001 * star.twinkleSpeed + star.twinklePhase);
      }

      // Parallax effect based on mouse position (disabled on mobile and reduced motion)
      let offsetX = 0;
      let offsetY = 0;
      if (!isMobile() && !reducedMotion) {
        const parallaxFactor = PARALLAX_STRENGTH * star.layer;
        offsetX = (mouseX - centerX) * parallaxFactor * -1;
        offsetY = (mouseY - centerY) * parallaxFactor * -1;
      }

      const x = star.x + offsetX;
      const y = star.y + offsetY;
      const opacity = star.opacity * twinkle * (isDarkMode ? 1 : 0.3);

      // Draw star with glow
      ctx.beginPath();
      ctx.arc(x, y, star.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(248, 250, 252, ${opacity})`;
      ctx.fill();

      // Add subtle glow for larger stars
      if (star.size > 1.2 && isDarkMode) {
        ctx.beginPath();
        ctx.arc(x, y, star.size * 2, 0, Math.PI * 2);
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, star.size * 2);
        gradient.addColorStop(0, `rgba(248, 250, 252, ${opacity * 0.3})`);
        gradient.addColorStop(1, 'rgba(248, 250, 252, 0)');
        ctx.fillStyle = gradient;
        ctx.fill();
      }
    });
  }, [isDarkMode, reducedMotion]);

  const drawParticles = useCallback((
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ) => {
    if (reducedMotion) return;

    const scrollOffset = scrollPositionRef.current * 0.1;

    particlesRef.current.forEach((particle) => {
      // Update position
      particle.x += particle.speedX;
      particle.y += particle.speedY + scrollOffset * 0.01;

      // Wrap around screen
      if (particle.x < 0) particle.x = width;
      if (particle.x > width) particle.x = 0;
      if (particle.y < 0) particle.y = height;
      if (particle.y > height) particle.y = 0;

      const opacity = particle.opacity * (isDarkMode ? 1 : 0.2);

      // Draw particle with soft glow
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      const gradient = ctx.createRadialGradient(
        particle.x, particle.y, 0,
        particle.x, particle.y, particle.size * 2
      );
      gradient.addColorStop(0, particle.color.replace(')', `, ${opacity})`).replace('rgb', 'rgba'));
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.fill();
    });
  }, [isDarkMode, reducedMotion]);

  const drawShootingStar = useCallback((
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ) => {
    if (!enableShootingStars || reducedMotion) return;

    const star = shootingStarRef.current;
    if (!star || !star.active) return;

    // Update position
    star.x += Math.cos(star.angle) * star.speed;
    star.y += Math.sin(star.angle) * star.speed;

    // Add to trail
    star.trail.unshift({ x: star.x, y: star.y, opacity: 1 });

    // Limit trail length and fade
    if (star.trail.length > 20) {
      star.trail.pop();
    }
    star.trail.forEach((point, i) => {
      point.opacity *= 0.85;
    });

    // Draw trail
    star.trail.forEach((point, i) => {
      const size = 2 * (1 - i / star.trail.length);
      const opacity = point.opacity * (isDarkMode ? 1 : 0.5);

      ctx.beginPath();
      ctx.arc(point.x, point.y, size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(248, 250, 252, ${opacity})`;
      ctx.fill();
    });

    // Draw head with golden glow
    ctx.beginPath();
    ctx.arc(star.x, star.y, 3, 0, Math.PI * 2);
    const headGradient = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, 8);
    headGradient.addColorStop(0, `rgba(251, 191, 36, ${isDarkMode ? 1 : 0.6})`);
    headGradient.addColorStop(0.5, `rgba(248, 250, 252, ${isDarkMode ? 0.8 : 0.4})`);
    headGradient.addColorStop(1, 'rgba(248, 250, 252, 0)');
    ctx.fillStyle = headGradient;
    ctx.fill();

    // Check if out of bounds
    if (star.x < -100 || star.x > width + 100 || star.y > height + 100) {
      star.active = false;
      shootingStarRef.current = null;
    }
  }, [enableShootingStars, isDarkMode, reducedMotion]);

  // ============================================================================
  // Main Animation Loop
  // ============================================================================

  const animate = useCallback((time: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw layers in order
    drawNebulaGradient(ctx, width, height, time);
    drawParticles(ctx, width, height);
    drawStars(ctx, width, height, time);
    drawShootingStar(ctx, width, height);

    // Check if it's time for a new shooting star
    if (enableShootingStars && !reducedMotion && !shootingStarRef.current) {
      const timeSinceLastStar = time - lastShootingStarTimeRef.current;
      if (timeSinceLastStar > nextShootingStarDelayRef.current) {
        createShootingStar(width, height);
        lastShootingStarTimeRef.current = time;
        nextShootingStarDelayRef.current = random(SHOOTING_STAR_INTERVAL_MIN, SHOOTING_STAR_INTERVAL_MAX);
      }
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [
    drawNebulaGradient,
    drawParticles,
    drawStars,
    drawShootingStar,
    createShootingStar,
    enableShootingStars,
    reducedMotion,
  ]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
    }

    // Reinitialize elements for new dimensions
    initializeStars(rect.width, rect.height);
    initializeParticles(rect.width, rect.height);
  }, [initializeStars, initializeParticles]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isMobile() || reducedMotion) return;
    mousePositionRef.current = { x: e.clientX, y: e.clientY };
  }, [reducedMotion]);

  const handleScroll = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
  }, []);

  // ============================================================================
  // Effects
  // ============================================================================

  // Check dark mode state
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };

    // Initial check
    checkDarkMode();

    // Watch for class changes on html element
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  // Check reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);

    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Initialize and start animation
  useEffect(() => {
    handleResize();

    // Start animation loop
    animationFrameRef.current = requestAnimationFrame(animate);

    // Add event listeners
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      // Cleanup
      cancelAnimationFrame(animationFrameRef.current);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleResize, handleMouseMove, handleScroll, animate]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{
        zIndex: 0,
        opacity: isDarkMode ? 1 : 0.2,
      }}
      aria-hidden="true"
    />
  );
};

export default CosmicBackground;
